import React from 'react';
import { CircularProgress, InputAdornment, MenuItem, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { RootState } from 'redux/reducers';
import AuthActionCreator from 'redux/creators/AuthActionCreators';
import { getAccessibleClients } from 'services/orgAdminClients';
import { Client } from '@eql-ai/typescript-types';

const useStyles = makeStyles(() => ({
    root: {
        '& label.Mui-focused': {
            color: 'white'
        },
        '& .MuiFormLabel-root': {
            color: 'white'
        },
        '& .MuiTextField-root': {
            background: 'green'
        },
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: 'white'
            },
            '&:hover fieldset': {
                borderColor: 'white'
            },
            '&.Mui-focused fieldset': {
                borderColor: 'white'
            }
        },
        width: 340
    },
    select: {
        '& .MuiSvgIcon-root': {
            color: 'white'
        },
        color: 'white'
    }
}));

const OrgClientDropdown = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const [clients, setClients] = React.useState<Client[]>([]);
    const [selectedClientId, setSelectedClientId] = React.useState<string>('');
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    const customer = useSelector((state: RootState) => state.AuthReducer.organisation);
    const region = useSelector((state: RootState) => state.AuthReducer.currentRegion);
    const loggedInUser = useSelector((state: RootState) => state.AuthReducer.user);

    React.useEffect(() => {
        setSelectedClientId(customer?.id || '');
    }, [customer]);

    const handleChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const nextId = event.target.value;
            const selectedClient = clients.find((client) => client.id === nextId);
            if (!selectedClient) {
                return;
            }

            setSelectedClientId(nextId);
            dispatch(AuthActionCreator.setCurrentOrganisation(selectedClient));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [clients]
    );

    React.useEffect(() => {
        if (!region || !loggedInUser) {
            return;
        }

        let isMounted = true;

        (async () => {
            try {
                setIsLoading(true);

                const { id: userId, org_id: orgId } = loggedInUser as any;
                const availableClients = await getAccessibleClients({ region, userId, orgId });

                if (!isMounted) {
                    return;
                }

                setClients(availableClients);

                const existingSelection = availableClients.find(
                    (client) => client.id === customer?.id
                );
                if (existingSelection) {
                    setSelectedClientId(existingSelection.id ?? '');
                    return;
                }

                const firstClient = availableClients[0];
                if (firstClient) {
                    setSelectedClientId(firstClient.id ?? '');
                    dispatch(AuthActionCreator.setCurrentOrganisation(firstClient));
                }
            } catch (error) {
                console.error(error);
                enqueueSnackbar('Unable to load tenants', { variant: 'error' });
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        })();

        return () => {
            isMounted = false;
        };
    }, [region, loggedInUser, customer?.id]);

    return (
        <TextField
            label="Tenants"
            variant="outlined"
            select
            size="small"
            className={classes.root}
            onChange={handleChange}
            value={selectedClientId}
            sx={{ marginRight: 2 }}
            slotProps={{
                input: {
                    margin: 'dense',
                    style: { color: 'white' },
                    endAdornment: isLoading ? (
                        <InputAdornment position="end">
                            <CircularProgress size={16} sx={{ color: 'white' }} />
                        </InputAdornment>
                    ) : null
                },
                select: { className: classes.select }
            }}
        >
            {isLoading && (
                <MenuItem value="loading" disabled sx={{ color: 'white' }}>
                    <CircularProgress size={14} sx={{ marginRight: 1, color: 'white' }} /> Loading
                    tenants...
                </MenuItem>
            )}
            {!isLoading && clients.length === 0 && (
                <MenuItem value="" disabled>
                    No tenants available
                </MenuItem>
            )}
            {clients.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                    {client.name}
                </MenuItem>
            ))}
        </TextField>
    );
};

export default OrgClientDropdown;
